import React, { FC, Fragment, ReactElement } from 'react';
import {
    useMediaQuery,
    useTheme,
    Text,
    Box,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Popover,
} from '@maas-components/core';
import { Close, ExitToApp, PowerInput as PowerInputIcon } from '@maas-components/icons';
import clsx from 'clsx';
import PopupState, { InjectedProps, bindPopover } from '@maas-components/x-popup-state';
import ProfileButton from './components/ProfileButton';
import SellerInfo from './components/SellerInfo';
import UserInfo from './components/UserInfo';
import useStyles from './styles';
import { Seller } from '../types';

export type AfterUserInfoProps = {
    /** function to be called on close button click */
    onClose: () => void;
};

export type ProfileProps = {
    /** the name to be displayed */
    name: string;
    /** the email to be displayed */
    email: string;
    /** function to be called on logout button click */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onLogout?: (...args: any) => void;
    /** an array of sellers to be displayed */
    sellers?: Seller[];
    /** the selected seller */
    currentSeller?: Seller;
    /** if the user information (name and email) should be displayed beside profile button */
    enableUserInfo?: boolean;
    /** If true, the color prop is applied in dark mode. */
    enableColorDark?: boolean;
    /** If true, shows option for changing password. */
    enableChangePassword?: boolean;
    /**  callback for changing password */
    onChangePassword?: (...args: unknown[]) => void;
    /** component to be rendered after user info */
    AfterUserInfoComponent?: (...args: any) => ReactElement | null;
};

const Profile: FC<ProfileProps> = ({
    name,
    email,
    onLogout,
    sellers,
    currentSeller,
    enableColorDark,
    enableUserInfo = false,
    onChangePassword,
    enableChangePassword = false,
    AfterUserInfoComponent = () => <></>,
}) => {
    const theme = useTheme();
    const mode = enableColorDark ? 'dark' : theme.palette.type;

    const classes = useStyles({ mode });
    const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
    const paperClass = clsx(classes.paper, {
        [classes.paperMobile]: isMobile,
    });

    const handleClose = (popupState: InjectedProps) => {
        popupState.close();
    };

    const handleLogout = (popupState: InjectedProps) => {
        popupState.close();
        sessionStorage.removeItem('maas-tenantId');
        onLogout?.();
    };

    const handleChangePassword = (popupState: InjectedProps) => {
        popupState.close();
        onChangePassword?.();
    };

    return (
        <PopupState variant="popover">
            {(popupState) => (
                <>
                    <ProfileButton
                        mode={mode}
                        enableUserInfo={enableUserInfo}
                        currentSeller={currentSeller}
                        name={name}
                        popupState={popupState}
                    />
                    <Popover
                        classes={{ paper: paperClass }}
                        {...bindPopover(popupState)}
                        anchorReference={isMobile ? 'anchorPosition' : 'anchorEl'}
                        anchorPosition={{ left: 0, top: 0 }}
                        marginThreshold={isMobile ? 0 : 16}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'center',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'center',
                        }}
                    >
                        <Box className={classes.root}>
                            {isMobile && (
                                <IconButton
                                    data-testid="close-button-test-id"
                                    className={classes.closeButton}
                                    onClick={() => {
                                        popupState.setOpen(false);
                                    }}
                                >
                                    <Close />
                                </IconButton>
                            )}
                            <List>
                                <UserInfo mode={mode} name={name} email={email} />
                                <AfterUserInfoComponent onClose={() => handleClose(popupState)} />
                                <Divider />
                                {sellers && sellers.length > 0 && (
                                    <Box py={2} pl={3}>
                                        <Text fontWeight="medium">Organizações</Text>
                                    </Box>
                                )}
                                {sellers &&
                                    currentSeller &&
                                    sellers.map((seller) => (
                                        <SellerInfo
                                            mode={mode}
                                            seller={seller}
                                            currentSeller={currentSeller}
                                            key={seller.tenantId}
                                        />
                                    ))}
                                {sellers && <Divider />}

                                {enableChangePassword && (
                                    <Box>
                                        <ListItem
                                            button
                                            className={classes.logoutListItem}
                                            onClick={() => handleChangePassword(popupState)}
                                        >
                                            <ListItemText
                                                primary={
                                                    <Box display="flex" alignItems="center">
                                                        <PowerInputIcon />
                                                        <Text component="span" fontWeight="bold">
                                                            Mudar senha
                                                        </Text>
                                                    </Box>
                                                }
                                            />
                                        </ListItem>
                                    </Box>
                                )}
                                <ListItem
                                    dense
                                    button
                                    className={classes.logoutListItem}
                                    onClick={() => handleLogout(popupState)}
                                >
                                    <ListItemText
                                        primary={
                                            <Box display="flex" alignItems="center">
                                                <ExitToApp />
                                                <Text component="span" fontWeight="bold" color="inherit">
                                                    Sair
                                                </Text>
                                            </Box>
                                        }
                                    />
                                </ListItem>
                            </List>
                        </Box>
                    </Popover>
                </>
            )}
        </PopupState>
    );
};

export default Profile;
